import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { ComposerHistory, type ComposerHistoryDirection } from './composer-history';
import { nudgeGate, sendNudge } from './nudge';
import {
  commandDiscussionMode,
  type CommandDiscussionMode,
} from './shared-command-timeline';
import type { MainCommandReplyTarget } from './main-command-recipients';
import type { MainCommandAcceptedEcho, MainCommandDeliveryRecord } from './main-command-delivery';
import { postMainCommandByRoute } from './local-command-route';
import type { MainCommandReplyRef } from './main-command-reply';

/**
 * The unsent Main Command draft, kept outside React state on purpose.
 *
 * It used to be state of this hook, which lives in the Operations Room, so every
 * keystroke re-rendered the whole desk - every count, panel and the full shared
 * timeline - and typing a long message froze the page. Only the surfaces that
 * show the draft subscribe to it; everything else reads it when it needs it.
 */
export interface ComposerDraftStore {
  readonly get: () => string;
  readonly set: (text: string) => void;
  readonly subscribe: (listener: () => void) => () => void;
}

export function createComposerDraftStore(initial = ''): ComposerDraftStore {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set: (text) => {
      if (text === value) return;
      value = text;
      for (const listener of [...listeners]) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
  };
}

/** The live draft, for a surface that shows it. Re-renders only that surface. */
export function useComposerDraft(controller: Pick<MainCommandComposerController, 'draft'>): string {
  return useSyncExternalStore(controller.draft.subscribe, controller.draft.get, controller.draft.get);
}

export interface MainCommandComposerController {
  /** The draft itself; subscribe with useComposerDraft to show it. */
  readonly draft: ComposerDraftStore;
  /** The draft as it is now, for a callback that needs it without rendering it. */
  readonly getContent: () => string;
  readonly sending: boolean;
  readonly sendError: string | null;
  readonly composerAuthority: 'command' | 'observer_discussion' | 'worker_chat' | 'multi_recipient' | 'none';
  readonly historyScope: string;
  readonly draftAt: number | null;
  readonly setContent: (text: string) => void;
  readonly updateContent: (update: (current: string) => string) => void;
  readonly recallHistory: (direction: ComposerHistoryDirection, current: string) => string | null;
  readonly setContentFromHistory: (text: string) => void;
  readonly historyWalking: () => boolean;
  readonly send: () => void;
  /**
   * #698 direct-send quick command: sends exactly `text` through the same path as
   * send(), leaving whatever the owner has typed in the composer untouched.
   */
  readonly sendText: (text: string) => void;
}

export function useMainCommandComposerController(input: {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly projectId: string;
  readonly draft: { readonly text: string; readonly at: number } | null;
  readonly onSent: () => Promise<void>;
  readonly plannerUrl: string;
  readonly discussion?: CommandDiscussionMode;
  readonly replyTargets: readonly MainCommandReplyTarget[];
  readonly workerReplyTargets: readonly string[];
  readonly observerThreadId: string | null;
  readonly discussionBusy?: boolean;
  readonly onDiscussionSent?: () => Promise<void>;
  readonly onDiscussionError?: (message: string) => void;
  readonly onAccepted?: (accepted: MainCommandAcceptedEcho) => void;
  /** Called synchronously when a command post is about to start, before the guard awaits. */
  readonly onBeforeSend?: () => void;
  /** UI-only @ syntax resolves into the existing canonical target arrays before send. */
  /** #743: the exact earlier message this owner message replies to, or null. */
  readonly replyTo?: MainCommandReplyRef | null;
  readonly resolveRecipients?: (content: string) => {
    readonly content: string;
    readonly targets: readonly MainCommandReplyTarget[];
    readonly workerIds: readonly string[];
  };
}): MainCommandComposerController {
  const {
    client, guard, orgId, projectId, draft, onSent, plannerUrl, discussion,
    replyTargets, workerReplyTargets, observerThreadId, discussionBusy = false,
    onDiscussionSent, onDiscussionError, onAccepted, onBeforeSend, resolveRecipients, replyTo = null,
  } = input;
  const chatMode = discussion ?? commandDiscussionMode();
  const observerSelected = replyTargets.some((target) => target === 'ux' || target === 'core');
  const workerSelected = workerReplyTargets.length > 0;
  const composerAuthority = replyTargets.includes('overseer')
    ? (observerSelected || workerSelected ? 'multi_recipient' : 'command')
    : observerSelected
      ? (workerSelected ? 'multi_recipient' : 'observer_discussion')
      : workerSelected ? 'worker_chat' : 'none';
  const [draftStore] = useState(() => createComposerDraftStore());
  const setContentState = draftStore.set;
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const lastNudge = useRef(0);
  const history = useRef(new ComposerHistory());
  const historyScope = `owner-command:${orgId}:${projectId || 'all-projects'}`;

  useEffect(() => {
    if (!draft) return;
    history.current.edit(historyScope);
    setContentState(draft.text);
  }, [draft, historyScope]);

  const setContent = useCallback((text: string) => {
    history.current.edit(historyScope);
    setContentState(text);
  }, [historyScope]);

  const updateContent = useCallback((update: (current: string) => string) => {
    history.current.edit(historyScope);
    setContentState(update(draftStore.get()));
  }, [historyScope, draftStore]);

  const completeSend = useCallback(async (text: string, clearDraft: boolean) => {
    history.current.record(historyScope, text);
    if (clearDraft) setContentState('');
    await onSent();
  }, [historyScope, onSent]);

  const sendContent = useCallback((raw: string, fromDraft: boolean) => {
    if (sending || discussionBusy || raw.trim() === ''
      || (replyTargets.length === 0 && workerReplyTargets.length === 0)) return;
    const resolved = resolveRecipients?.(raw) ?? {
      content: raw.trim(), targets: replyTargets, workerIds: workerReplyTargets,
    };
    const text = resolved.content;
    const routedTargets = resolved.targets;
    const routedWorkerIds = resolved.workerIds;
    const routedObserverSelected = routedTargets.some((target) => target === 'ux' || target === 'core');
    if (text === '' || (routedTargets.length === 0 && routedWorkerIds.length === 0)) return;
    const finishSend = (sent: string) => completeSend(sent, fromDraft);
    const requestId = crypto.randomUUID();
    onBeforeSend?.();
    void guard('post_owner_command', async () => {
      setSending(true);
      setSendError(null);
      try {
        type PostMainCommandResult = {
          ok?: boolean;
          value?: {
            commandId?: string;
            seq?: number;
            managementDeliveries?: readonly MainCommandDeliveryRecord[];
            workerDeliveries?: readonly MainCommandDeliveryRecord[];
            routedAs?: string;
            jobWorkerNumber?: number;
          };
          error?: { code?: string };
        };
        let result: PostMainCommandResult | null = null;
        let transportMessage: string | null = null;
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const response = await postMainCommandByRoute(client, {
              orgId,
              projectId: projectId || null,
              content: text,
              targets: routedTargets,
              workerIds: routedWorkerIds,
              threadId: routedObserverSelected ? observerThreadId : null,
              requestId,
              ...(replyTo === null ? {} : { replyTo: { kind: replyTo.kind, id: replyTo.id } }),
          });
          result = response.data as PostMainCommandResult | null;
          transportMessage = response.error;
          if (response.error === null || result?.ok) break;
        }
        if (!result?.ok) {
          const message = transportMessage ?? result?.error?.code ?? 'Main Command message was not accepted.';
          setSendError(message);
          onDiscussionError?.(message);
          return;
        }

        const commandId = result.value?.commandId;
        if (typeof commandId === 'string' && commandId !== '') {
          onAccepted?.({
            commandId,
            requestId,
            seq: typeof result.value?.seq === 'number' ? result.value.seq : null,
            text,
            targets: [...routedTargets],
            workerIds: [...routedWorkerIds],
            managementDeliveries: result.value?.managementDeliveries ?? [],
            workerDeliveries: result.value?.workerDeliveries ?? [],
            routedAs: typeof result.value?.routedAs === 'string' ? result.value.routedAs : null,
            jobWorkerNumber: typeof result.value?.jobWorkerNumber === 'number' ? result.value.jobWorkerNumber : null,
          });
        }

        if (routedTargets.includes('overseer')) {
          const gate = nudgeGate({ projectId, content: text, now: Date.now(), lastAt: lastNudge.current });
          lastNudge.current = gate.lastAt;
          if (gate.send) sendNudge(plannerUrl);
        }
        await finishSend(text);
        if (routedObserverSelected) await onDiscussionSent?.();
      } finally {
        setSending(false);
      }
    });
  }, [
    sending, discussionBusy, replyTargets, workerReplyTargets, guard, orgId,
    projectId, client, observerSelected, observerThreadId, plannerUrl,
    completeSend, onDiscussionSent, onDiscussionError, onAccepted, onBeforeSend, resolveRecipients, replyTo,
  ]);

  const send = useCallback(() => sendContent(draftStore.get(), true), [sendContent, draftStore]);
  const sendText = useCallback((text: string) => sendContent(text, false), [sendContent]);

  return {
    draft: draftStore,
    getContent: draftStore.get,
    sending,
    sendError,
    composerAuthority,
    historyScope,
    draftAt: draft?.at ?? null,
    setContent,
    updateContent,
    recallHistory: (direction, current) => history.current.recall(historyScope, direction, current),
    setContentFromHistory: (text) => setContentState(text),
    historyWalking: () => history.current.isWalking(historyScope),
    send,
    sendText,
  };
}
